import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, Network } from "lucide-react";
import { useDebateStore } from "../../store/debateStore";
import type { DebateTopology } from "../../domain/types";
import { cn } from "../common/cn";
import { Card, CardHeader } from "../common/Card";
import { TopologyDiagram } from "./TopologyDiagram";
import { t } from "../../i18n";

const TOPOLOGIES: readonly DebateTopology[] = [
  "round-robin",
  "cross-fire",
  "moderator-led",
  "diverse-poles",
  "solo-then-debate",
  "investigation",
];

interface TopologyPickerProps {
  disabled?: boolean;
}

export function TopologyPicker({ disabled }: TopologyPickerProps) {
  const reduceMotion = useReducedMotion();
  const topology = useDebateStore((s) => s.topology);
  const setTopology = useDebateStore((s) => s.setTopology);
  const status = useDebateStore((s) => s.status);
  const isLocked = disabled || status === "running" || status === "provisioning";

  return (
    <Card tone="elevated" aria-labelledby="topology-title">
      <CardHeader
        icon={<Network className="h-4 w-4" aria-hidden />}
        title={<span id="topology-title">{t("setup.topologyTitle")}</span>}
        subtitle={t("setup.topologyHint")}
      />
      <div role="radiogroup" aria-labelledby="topology-title" className="flex flex-col gap-2 p-3 sm:p-4">
        {TOPOLOGIES.map((id) => {
          const active = topology === id;
          const label = t(`setup.topology.${id}.label` as const);
          const tagline = t(`setup.topology.${id}.tagline` as const);
          const bestFor = t(`setup.topology.${id}.bestFor` as const);

          return (
            <button
              key={id}
              role="radio"
              aria-checked={active}
              type="button"
              disabled={isLocked}
              onClick={() => setTopology(id)}
              className={cn(
                "group relative flex items-stretch gap-3 rounded-xl border p-3 text-left transition focus-ring",
                "hover:-translate-y-[1px] hover:shadow-elev2",
                active
                  ? "border-[hsl(var(--accent)/0.7)] bg-[linear-gradient(135deg,hsl(var(--accent)/0.12),hsl(var(--accent-2)/0.08))]"
                  : "border-line-soft/70 bg-surface-2/40 hover:border-[hsl(var(--accent)/0.45)] hover:bg-surface-2/70",
                isLocked && "cursor-not-allowed opacity-60 hover:translate-y-0",
              )}
            >
              <div
                className={cn(
                  "flex flex-none items-center justify-center rounded-lg border p-1",
                  active
                    ? "border-[hsl(var(--accent)/0.5)] bg-surface-0/40"
                    : "border-line-soft/60 bg-surface-0/30",
                )}
              >
                <TopologyDiagram topology={id} active={active} size={64} />
              </div>
              <div className="flex flex-1 flex-col justify-between gap-1.5 py-0.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-fg-strong">{label}</span>
                  {active ? (
                    <motion.span
                      layoutId="topology-check"
                      transition={
                        reduceMotion
                          ? { duration: 0 }
                          : { type: "spring", stiffness: 320, damping: 28 }
                      }
                      className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-accent-300"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                      Seçildi
                    </motion.span>
                  ) : null}
                </div>
                <p className="text-xs leading-snug text-fg-muted">{tagline}</p>
                <p className="text-[11px] text-fg-subtle">{bestFor}</p>
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
